/**
 * Sécurité du compte : 2FA, codes de récupération, mot de passe, adresse e-mail,
 * sessions actives, suppression du compte.
 *
 * Les actions sensibles passent par `useSensitiveAction`, qui déclenche une
 * confirmation d'identité quand le serveur l'exige puis rejoue l'action.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { authApi } from "../api/endpoints";
import type { RecoveryCodes } from "../api/types";
import { Modal } from "../components/Modal";
import {
  ConfirmPhraseModal,
  useSensitiveAction,
} from "../components/ReauthDialog";
import { useToast } from "../components/ToastContext";
import {
  Alert,
  Button,
  Card,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
  TextField,
} from "../components/ui";
import { useSession } from "../hooks/SessionContext";
import { useDocumentTitle, useMutation, useResource } from "../hooks/useApi";
import { describeUserAgent, formatDateTime } from "../lib/format";

export function SecurityPage() {
  useDocumentTitle("Sécurité");
  const toast = useToast();
  const navigate = useNavigate();
  const { user, refresh, signOut } = useSession();

  const sessions = useResource(() => authApi.sessions(), []);

  const [codes, setCodes] = useState<RecoveryCodes | null>(null);
  const [totpSetupOpen, setTotpSetupOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const totpMethod = user?.twofa_methods.find((m) => m.method_type === "totp");
  const emailMethod = user?.twofa_methods.find((m) => m.method_type === "email");

  /* --- Activation TOTP ------------------------------------------------- */
  const setup = useResource(() => authApi.totpSetup(), [totpSetupOpen], {
    enabled: totpSetupOpen,
  });
  const [totpCode, setTotpCode] = useState("");
  const confirmTotp = useMutation(() => authApi.totpConfirm(totpCode.trim()), {
    onSuccess: async (result) => {
      setTotpSetupOpen(false);
      setTotpCode("");
      setCodes(result);
      await refresh();
      toast.success("Application d'authentification activée");
    },
  });

  /* --- Actions sensibles ----------------------------------------------- */
  const disableTotp = useSensitiveAction(
    async () => {
      await authApi.totpDisable();
      await refresh();
      toast.success("Application d'authentification désactivée");
    },
    { onError: (error) => toast.error("Désactivation refusée", error.message) },
  );

  const disableEmailTwofa = useSensitiveAction(
    async () => {
      await authApi.emailTwoFactorDisable();
      await refresh();
      toast.success("Second facteur par e-mail désactivé");
    },
    { onError: (error) => toast.error("Désactivation refusée", error.message) },
  );

  const regenerate = useSensitiveAction(
    async () => {
      const result = await authApi.regenerateRecoveryCodes();
      setCodes(result);
      await refresh();
    },
    { onError: (error) => toast.error("Régénération refusée", error.message) },
  );

  const [newEmail, setNewEmail] = useState("");
  const changeEmail = useSensitiveAction(
    async () => {
      const response = await authApi.changeEmail(newEmail.trim());
      setNewEmail("");
      toast.success("Confirmation envoyée", response.message);
    },
    { onError: (error) => toast.error("Changement refusé", error.message) },
  );

  /* --- Second facteur e-mail ------------------------------------------- */
  const enableEmailTwofa = useMutation(() => authApi.emailTwoFactorEnable(), {
    onSuccess: async (result) => {
      if ("codes" in result) setCodes(result as RecoveryCodes);
      await refresh();
      toast.success("Second facteur par e-mail activé");
    },
    onError: (error) => toast.error("Activation refusée", error.message),
  });

  /* --- Mot de passe ---------------------------------------------------- */
  const [currentPassword, setCurrentPassword] = useState("");
  const [nextPassword, setNextPassword] = useState("");
  const [nextConfirmation, setNextConfirmation] = useState("");
  const changePassword = useMutation(
    () =>
      authApi.changePassword({
        current_password: currentPassword,
        new_password: nextPassword,
      }),
    {
      onSuccess: async (response) => {
        setCurrentPassword("");
        setNextPassword("");
        setNextConfirmation("");
        await sessions.reload();
        toast.success("Mot de passe modifié", response.message);
      },
      onError: (error) => toast.error("Changement refusé", error.message),
    },
  );

  /* --- Sessions -------------------------------------------------------- */
  const revokeOthers = useMutation(() => authApi.revokeOtherSessions(), {
    onSuccess: async (response) => {
      await sessions.reload();
      toast.success("Sessions déconnectées", response.message);
    },
  });
  const revokeOne = useMutation((id: string) => authApi.revokeSession(id), {
    onSuccess: async () => {
      await sessions.reload();
      toast.success("Session déconnectée");
    },
  });

  /* --- Suppression du compte ------------------------------------------- */
  const deleteAccount = useSensitiveAction(
    async () => {
      await authApi.deleteAccount("SUPPRIMER MON COMPTE");
      await signOut();
      navigate("/connexion", { replace: true });
    },
    { onError: (error) => toast.error("Suppression refusée", error.message) },
  );

  if (!user) return <LoadingBlock />;

  const passwordMismatch =
    nextConfirmation.length > 0 && nextConfirmation !== nextPassword
      ? "Les deux mots de passe ne correspondent pas."
      : undefined;

  return (
    <>
      <PageHeader
        title="Sécurité"
        subtitle="Vérification en deux étapes, mot de passe, adresse e-mail et sessions actives."
      />

      <div className="stack">
        {!user.has_twofa && (
          <Alert tone="warning" title="Votre compte n'est pas protégé par un second facteur">
            Activez l'application d'authentification (recommandé) ou le code par
            e-mail pour empêcher une connexion avec votre seul mot de passe.
          </Alert>
        )}

        {/* --- 2FA --------------------------------------------------------- */}
        <Card
          title="Application d'authentification — Recommandé"
          hint="Compatible Google Authenticator, Microsoft Authenticator, Authy, Bitwarden, 1Password et autres applications TOTP."
        >
          {totpMethod?.enabled ? (
            <>
              <p>
                <span className="badge badge--success">Activée</span>{" "}
                {totpMethod.confirmed_at && (
                  <span className="inline-note">
                    depuis le {formatDateTime(totpMethod.confirmed_at)}
                  </span>
                )}
              </p>
              <Button
                variant="danger"
                loading={disableTotp.pending}
                onClick={() => void disableTotp.run()}
              >
                Désactiver
              </Button>
            </>
          ) : (
            <>
              <p className="text-muted">
                Un code à 6 chiffres sera demandé à chaque connexion, en plus de
                votre mot de passe.
              </p>
              <Button variant="primary" onClick={() => setTotpSetupOpen(true)}>
                Activer l'application d'authentification
              </Button>
            </>
          )}
        </Card>

        <Card
          title="Code par e-mail — Standard"
          hint="Un code temporaire est envoyé à votre adresse à chaque connexion."
        >
          {emailMethod?.enabled ? (
            <>
              <p>
                <span className="badge badge--success">Activé</span>{" "}
                <span className="inline-note">sur {user.email}</span>
              </p>
              <Button
                variant="danger"
                loading={disableEmailTwofa.pending}
                onClick={() => void disableEmailTwofa.run()}
              >
                Désactiver
              </Button>
            </>
          ) : (
            <>
              <p className="text-muted">
                Moins robuste que l'application d'authentification (votre boîte
                e-mail devient le point faible), mais plus simple à mettre en place.
              </p>
              <Button
                variant="ghost"
                loading={enableEmailTwofa.pending}
                onClick={() => void enableEmailTwofa.run()}
                disabled={!user.email_verified}
              >
                Activer le code par e-mail
              </Button>
              {!user.email_verified && (
                <p className="inline-note mt-3 mb-0">
                  Vérifiez d'abord votre adresse e-mail.
                </p>
              )}
            </>
          )}
        </Card>

        <Card
          title="Codes de récupération"
          hint="À utiliser si vous perdez l'accès à votre second facteur. Chaque code ne fonctionne qu'une fois."
        >
          <p>
            Codes encore utilisables : <strong>{user.recovery_codes_remaining}</strong>
          </p>
          {user.recovery_codes_remaining <= 2 && user.has_twofa && (
            <Alert tone="warning" title="Il vous reste peu de codes">
              Régénérez une nouvelle liste et conservez-la en lieu sûr.
            </Alert>
          )}
          <Button
            variant="ghost"
            loading={regenerate.pending}
            onClick={() => void regenerate.run()}
          >
            Régénérer les codes de récupération
          </Button>
          <p className="inline-note mt-3 mb-0">
            Les codes précédents deviendront immédiatement invalides.
          </p>
        </Card>

        {/* --- Mot de passe ----------------------------------------------- */}
        <Card title="Mot de passe">
          <ErrorAlert error={changePassword.error} title="Changement refusé" />
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!passwordMismatch) void changePassword.run();
            }}
            noValidate
          >
            <TextField
              label="Mot de passe actuel"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              error={changePassword.fieldErrors.current_password}
            />
            <TextField
              label="Nouveau mot de passe"
              type="password"
              autoComplete="new-password"
              required
              value={nextPassword}
              onChange={(event) => setNextPassword(event.target.value)}
              error={changePassword.fieldErrors.new_password}
              hint="Au moins 12 caractères, avec au moins 3 types de caractères différents."
            />
            <TextField
              label="Confirmation"
              type="password"
              autoComplete="new-password"
              required
              value={nextConfirmation}
              onChange={(event) => setNextConfirmation(event.target.value)}
              error={passwordMismatch}
            />
            <Button
              type="submit"
              variant="primary"
              loading={changePassword.pending}
              disabled={
                !currentPassword || !nextPassword || Boolean(passwordMismatch)
              }
            >
              Changer le mot de passe
            </Button>
            <p className="inline-note mt-3 mb-0">
              Vos autres sessions seront déconnectées par sécurité.
            </p>
          </form>
        </Card>

        {/* --- Adresse e-mail --------------------------------------------- */}
        <Card
          title="Adresse e-mail"
          hint={`Adresse actuelle : ${user.email}${user.email_verified ? " (vérifiée)" : " (non vérifiée)"}`}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void changeEmail.run();
            }}
            noValidate
          >
            <TextField
              label="Nouvelle adresse e-mail"
              type="email"
              autoComplete="email"
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
              hint="Votre adresse actuelle reste active jusqu'à confirmation de la nouvelle."
            />
            <Button
              type="submit"
              variant="ghost"
              loading={changeEmail.pending}
              disabled={newEmail.trim().length < 4}
            >
              Demander le changement
            </Button>
          </form>
        </Card>

        {/* --- Sessions ---------------------------------------------------- */}
        <Card
          title="Sessions actives"
          actions={
            <Button
              variant="ghost"
              size="sm"
              loading={revokeOthers.pending}
              onClick={() => void revokeOthers.run()}
            >
              Déconnecter toutes les autres sessions
            </Button>
          }
          flush
        >
          {sessions.loading ? (
            <LoadingBlock />
          ) : !sessions.data || sessions.data.length === 0 ? (
            <p className="text-muted" style={{ padding: "1rem" }}>
              Aucune session active.
            </p>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <caption className="visually-hidden">Sessions actives</caption>
                <thead>
                  <tr>
                    <th scope="col">Appareil</th>
                    <th scope="col">Adresse IP</th>
                    <th scope="col">Dernière activité</th>
                    <th scope="col">Expire le</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.data.map((session) => (
                    <tr key={session.id}>
                      <td>
                        {describeUserAgent(session.user_agent)}
                        {session.is_current && (
                          <>
                            {" "}
                            <span className="badge badge--accent">
                              Session actuelle
                            </span>
                          </>
                        )}
                      </td>
                      <td className="mono">{session.ip_address ?? "—"}</td>
                      <td className="nowrap">
                        {formatDateTime(session.last_seen_at ?? session.created_at)}
                      </td>
                      <td className="nowrap">{formatDateTime(session.expires_at)}</td>
                      <td>
                        {!session.is_current && (
                          <Button
                            size="sm"
                            variant="quiet"
                            onClick={() => void revokeOne.run(session.id)}
                          >
                            Déconnecter
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* --- Suppression du compte -------------------------------------- */}
        <Card title="Supprimer mon compte">
          <Alert tone="critical" title="Action difficilement réversible">
            Votre compte et votre entreprise seront désactivés. Téléchargez
            d'abord un export complet de vos données depuis la page « Données et
            sauvegardes ».
          </Alert>
          <Button variant="danger" onClick={() => setDeleteOpen(true)}>
            Supprimer mon compte
          </Button>
        </Card>
      </div>

      {/* --- Modales ------------------------------------------------------- */}
      {disableTotp.dialog}
      {disableEmailTwofa.dialog}
      {regenerate.dialog}
      {changeEmail.dialog}
      {deleteAccount.dialog}

      <Modal
        open={totpSetupOpen}
        title="Activer l'application d'authentification"
        onClose={() => {
          setTotpSetupOpen(false);
          setTotpCode("");
        }}
        actions={
          <Button
            variant="primary"
            loading={confirmTotp.pending}
            disabled={totpCode.trim().length < 6}
            onClick={() => void confirmTotp.run()}
          >
            Confirmer et activer
          </Button>
        }
      >
        {setup.loading ? (
          <LoadingBlock />
        ) : setup.error ? (
          <ErrorAlert error={setup.error} title="Préparation impossible" />
        ) : setup.data ? (
          <>
            <p>{setup.data.instructions}</p>
            <div
              className="qr-holder"
              // Le SVG est généré par notre serveur à partir du secret : il ne
              // transite par aucun service tiers.
              dangerouslySetInnerHTML={{ __html: setup.data.qr_code_svg }}
            />
            <p className="inline-note">
              Impossible de scanner ? Saisissez cette clé manuellement :
            </p>
            <p className="secret-display">{setup.data.secret}</p>
            <ErrorAlert error={confirmTotp.error} title="Code refusé" />
            <TextField
              label="Code affiché par votre application"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totpCode}
              onChange={(event) => setTotpCode(event.target.value)}
              hint="L'activation ne prend effet qu'après cette confirmation."
            />
          </>
        ) : null}
      </Modal>

      <Modal
        open={codes !== null}
        title="Vos codes de récupération"
        onClose={() => setCodes(null)}
        closeLabel="J'ai conservé ces codes"
      >
        {codes && (
          <>
            <Alert tone="warning" title="Ils ne seront plus jamais affichés">
              {codes.warning}
            </Alert>
            <div className="recovery-codes">
              {codes.codes.map((code) => (
                <span key={code}>{code}</span>
              ))}
            </div>
            <Button
              variant="ghost"
              onClick={() => {
                const blob = new Blob(
                  [
                    "Codes de récupération ArkVibes Gestion\n",
                    `Générés le ${formatDateTime(codes.generated_at)}\n`,
                    "Chaque code ne fonctionne qu'une seule fois.\n\n",
                    ...codes.codes.map((code) => `${code}\n`),
                  ],
                  { type: "text/plain;charset=utf-8" },
                );
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "arkvibes-codes-de-recuperation.txt";
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              Télécharger les codes
            </Button>
          </>
        )}
      </Modal>

      <ConfirmPhraseModal
        open={deleteOpen}
        title="Supprimer définitivement mon compte"
        description={
          <p>
            Cette action désactive votre compte et votre entreprise. Assurez-vous
            d'avoir téléchargé un export complet de vos données.
          </p>
        }
        phrase="SUPPRIMER MON COMPTE"
        confirmLabel="Supprimer mon compte"
        danger
        pending={deleteAccount.pending}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => {
          setDeleteOpen(false);
          void deleteAccount.run();
        }}
      />
    </>
  );
}
